if (!customElements.get('blog-search')) {
  let _initialSearchDone = false;

  class BlogSearch extends HTMLElement {
    connectedCallback() {
      this._input   = this.querySelector('.blog-search-input');
      this._spinner = this.querySelector('.blog-search-spinner');
      this._btn     = this.querySelector('.blog-search-btn');
      this._form    = this.querySelector('.blog-search-form');

      const targetId = this.getAttribute('articles-target') || 'blog-articles';
      this._grid = document.getElementById(targetId);

      if (!this._input || !this._grid) return;

      this._originalHTML  = this._grid.innerHTML;
      this._currentQuery  = '';
      this._timer         = null;
      this._abortCtrl     = null;

      // Build image map from liquid-rendered cards.
      // Read from the actual <img> element so the browser gives us a fully-resolved
      // absolute URL — more reliable than the protocol-relative data-image attribute.
      this._imageMap = new Map();
      this._grid.querySelectorAll('[data-blog-card]').forEach(card => {
        const url    = card.getAttribute('data-url');
        const imgEl  = card.querySelector('img');
        const imgSrc = imgEl?.src || card.getAttribute('data-image') || '';
        if (url && imgSrc) this._imageMap.set(url, imgSrc);
      });

      this._layoutWrapper = document.querySelector('.blog-layout-wrapper');
      this._pagination    = document.getElementById('pagination');

      this._input.addEventListener('input', this._onInput.bind(this));
      this._form && this._form.addEventListener('submit', this._onSubmit.bind(this));

      const initialQ = new URLSearchParams(window.location.search).get('q');
      if (initialQ) {
        this._input.value = initialQ;
        if (!_initialSearchDone) {
          _initialSearchDone = true;
          this._layoutWrapper?.classList.add('is-searching');
          this._showSpinner();
          this._search(initialQ);
        }
      }
    }

    disconnectedCallback() {
      clearTimeout(this._timer);
      this._abortCtrl?.abort();
    }

    _onInput() {
      clearTimeout(this._timer);
      const q = this._input.value.trim();

      if (q === '') {
        this._abortCtrl?.abort();
        this._hideSpinner();
        this._restore();
        return;
      }

      this._layoutWrapper?.classList.add('is-searching');
      this._showSpinner();
      this._timer = setTimeout(() => this._search(q), 350);
    }

    _onSubmit(e) {
      e.preventDefault();
      const q = this._input.value.trim();
      if (q) window.location = `${window.location.pathname}?q=${encodeURIComponent(q)}`;
    }

    async _search(query) {
      this._currentQuery = query;
      this._abortCtrl?.abort();
      this._abortCtrl = new AbortController();

      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=article&resources[limit]=20`;
        const res  = await fetch(url, { signal: this._abortCtrl.signal });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        if (this._currentQuery !== query) return;

        const data     = await res.json();
        const articles = data?.resources?.results?.articles ?? [];

        this._render(articles, query);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this._hideSpinner();
      }
    }

    _render(articles, query) {
      if (articles.length === 0) {
        this._grid.innerHTML = `<p class="blog-search-empty">No results found for "<strong>${this._escape(query)}</strong>".</p>`;
      } else {
        this._grid.innerHTML = articles.map(a => this._cardHTML(a)).join('');
      }
      if (this._pagination) this._pagination.style.display = 'none';
      this._hideSpinner();
    }

    _restore() {
      this._grid.innerHTML = this._originalHTML;
      this._grid.querySelectorAll('[data-blog-card]').forEach(c => c.classList.add('is-visible'));
      this._layoutWrapper?.classList.remove('is-searching');
      if (this._pagination) {
        this._pagination.style.display = '';
        this._pagination.querySelectorAll('a[href]').forEach(a => {
          try {
            const u = new URL(a.href);
            u.searchParams.delete('q');
            a.href = u.toString();
          } catch {}
        });
      }
      if (new URLSearchParams(window.location.search).has('q')) {
        const u = new URL(window.location.href);
        u.searchParams.delete('q');
        history.replaceState(null, '', u.toString());
      }
    }

    _cardHTML(a) {
      const imgSrc  = this._resolveImage(a);
      const imgHTML = imgSrc
        ? `<img src="${imgSrc}" alt="${this._escape(a.title)}" width="100%" loading="lazy">`
        : '';
      const byline = [
        a.author ? `By ${this._escape(a.author)}` : '',
        a.published_at ? this._formatDate(a.published_at) : ''
      ].filter(Boolean).join(' | ');

      return `<div class="blog-card is-visible" data-blog-card>
        <div class="blog-card__image">
          <a href="${a.url}" tabindex="-1" aria-hidden="true">${imgHTML}</a>
        </div>
        <div class="blog-card__body">
          ${byline ? `<p class="blog-card__byline">${byline}</p>` : ''}
          <h3 class="blog-card__title"><a href="${a.url}">${this._escape(a.title)}</a></h3>
        </div>
      </div>`;
    }

    // Resolve the best available image URL for a search-result article.
    // Priority: liquid DOM map → Predictive Search API image → article body fallback.
    _resolveImage(a) {
      // 1. Liquid-rendered <img> from current page (always correct, browser-resolved URL)
      if (this._imageMap?.has(a.url)) return this._imageMap.get(a.url);

      // 2. Featured image from the Predictive Search API.
      //    The API may return `image` as:
      //      - an object with `.url`  (newer Shopify API convention)
      //      - an object with `.src`  (older convention)
      //      - a plain string URL
      //    It also returns `image: {}` (empty object) when no featured image is set.
      const raw = a.image ?? a.featured_image ?? null;
      if (raw) {
        const apiSrc =
          (typeof raw === 'string' ? raw : null) ||
          raw.url || raw.src || raw.cdn_url || null;
        if (apiSrc) return this._normalizeUrl(apiSrc);
      }

      // 3. First <img src> found in the article body (mirrors the Liquid fallback
      //    used in main-blog.liquid for articles without a featured image)
      if (a.body) {
        const match = a.body.match(/src="([^"]+)"/);
        if (match?.[1]) return this._normalizeUrl(match[1]);
      }

      return null;
    }

    // Ensure protocol-relative URLs work in all contexts
    _normalizeUrl(src) {
      return src.startsWith('//') ? `https:${src}` : src;
    }

    _formatDate(iso) {
      try {
        return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
      } catch {
        return '';
      }
    }

    _escape(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    _showSpinner() {
      this._spinner?.classList.add('is-active');
      if (this._btn) this._btn.style.display = 'none';
    }

    _hideSpinner() {
      this._spinner?.classList.remove('is-active');
      if (this._btn) this._btn.style.display = '';
    }
  }

  customElements.define('blog-search', BlogSearch);
}
